import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import BrandAndroid from 'components/Icons/BrandAndroid'
import BrandApple from 'components/Icons/BrandApple'
import Lock from 'components/Icons/Lock'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useState } from 'react'
import { StyleObject } from 'styletron-react'
import { solidBorder } from '../styleHelpers'
import AndroidLockScreen from './AndroidLockScreen'
import AppVersionNotice from './AppVersionNotice'
import InAppTracker from './InAppTracker'
import IosLockScreen from './IosLockScreen'
import LiveActivityCard from './LiveActivityCard'
import PushDisabledNotice from './PushDisabledNotice'
import PushNotificationCard from './PushNotificationCard'
import type {
  PreviewContent,
  PreviewPlatform,
  PreviewTab,
  TrackerStage,
} from './types'

const t = buildTranslate('live_updates.preview')

const PUSH_TAB_ID = 'lu-preview-tab-push'
const IN_APP_TAB_ID = 'lu-preview-tab-in-app'
const PANEL_ID = 'lu-preview-panel'
const STATUS_DOT_SIZE = '8px'
// FR-76: the phone mock behind the locked overlay is faded to a near-invisible
// backdrop — it's decorative chrome, not a real preview of merchant edits.
const LOCKED_MOCK_OPACITY = 0.2

export interface LiveUpdatesPreviewProps {
  content: PreviewContent
  // Whether the focused configuration can deliver (forces the 4-step tracker).
  deliveryCapable: boolean
  // The merchant's display name, rendered in the Live Activity card's Android
  // header ("{merchantName} · {eta}"). Omitted falls back to the ETA alone.
  merchantName?: string
  // The merchant's uploaded logo, used as the app icon in the push and Live
  // Activity mocks. Omitted (falls back to the brand-red placeholder square)
  // when the merchant has no logo uploaded.
  appIconUrl?: string
  // Tracker stage keys (e.g. "preparing") whose statuses are ALL disabled —
  // hidden as a middle track step in the Live Activity card and In-App
  // tracker. The Placed/Ready/Delivered endpoints always render regardless.
  disabledStages?: Set<TrackerStage>
  // FR-76: the whole configuration is not configurable (external/unsupported
  // ordering, or every stage disabled). Replaces the entire interactive
  // preview (tabs, platform toggle, "preview as you edit" row) with a faded,
  // non-interactive In-App mock and a locked overlay card (Figma 1000:4376 /
  // 1087:16091).
  locked?: boolean
  // Lock-screen delivery is off: In-App leads, push tab carries a notice.
  pushDisabled?: boolean
  // Whether the focused configuration's status provider is Flybuy — shows the
  // Flybuy check-in mock row in the In-App tab only (Figma 577:7432).
  isFlybuyProvider?: boolean
}

// Sticky right-hand phone preview for the Live Updates config page. Mirrors the
// focused status in real time across two tabs (Push & Live Activity / In-App)
// and two platforms (iOS / Android). Tab + platform are local UI state.
const LiveUpdatesPreview = ({
  content,
  deliveryCapable,
  merchantName,
  appIconUrl,
  disabledStages = new Set(),
  locked = false,
  pushDisabled = false,
  isFlybuyProvider = false,
}: LiveUpdatesPreviewProps) => {
  const [css] = useStyletron()
  const [tab, setTab] = useState<PreviewTab>(
    pushDisabled ? 'in_app' : 'push_live_activity'
  )
  const [platform, setPlatform] = useState<PreviewPlatform>('ios')

  // LaunchDarkly resolves after the first paint, so the seed above can miss the
  // flag turning on; only forcing on `true` keeps the push tab clickable after.
  useEffect(() => {
    if (pushDisabled) setTab('in_app')
  }, [pushDisabled])

  const isPushTab = tab === 'push_live_activity'
  const hasPush = content.channels.includes('push')
  const hasLiveActivity = content.channels.includes('live_activity')

  // Native <button> is used for the tab pills because uikit's Button does not
  // accept `role`, and the tabbed-interface a11y rule requires role="tab".
  // Reset styles mirror a segmented control (blue when active per Figma).
  const tabPillClass = (isActive: boolean, side: 'left' | 'right') =>
    css({
      flex: 1,
      cursor: 'pointer',
      padding: '8px 12px',
      fontSize: '14px',
      backgroundColor: isActive ? palette.belizeHole10 : palette.white,
      border: `1px solid ${isActive ? palette.belizeHole : palette.grey20}`,
      color: isActive ? palette.belizeHole : palette.grey70,
      fontWeight: isActive ? 700 : 400,
      borderTopLeftRadius: side === 'left' ? '4px' : 0,
      borderBottomLeftRadius: side === 'left' ? '4px' : 0,
      borderTopRightRadius: side === 'right' ? '4px' : 0,
      borderBottomRightRadius: side === 'right' ? '4px' : 0,
    })

  // Segmented platform toggle (Figma 368:6868): two compact badges — active =
  // belizeHole10 fill + belizeHole border/icon, inactive = white fill + grey20
  // border + grey70 icon; outer corners rounded 4px, square where they meet.
  // EVERY visual prop goes through `overrides.BaseButton.style`, not className:
  // uikit's BaseButton emits its own background/border atomic classes that land
  // AFTER a plain className in the Styletron cascade, so a `className`
  // background silently loses — confirmed live, the inactive button rendered
  // solid belizeHole (uikit's class) instead of the intended white. The
  // override replaces uikit's BaseButton style function outright and is the one
  // place that reliably wins. Longhand border props only — mixing the `border`
  // shorthand with baseui's longhand defaults trips Styletron's
  // "unsupported with atomic rendering" warning. The :hover echo keeps uikit's
  // own minimal-button hover from re-introducing the blue fill.
  const platformButtonOverrides = (
    isActive: boolean,
    side: 'left' | 'right'
  ) => {
    const borderColor = isActive ? palette.belizeHole : palette.grey20
    const backgroundColor = isActive ? palette.belizeHole10 : palette.white
    // solidBorder keeps the longhand-border workaround (vs uikit's BaseButton
    // resets) in one place; StyleObject typing lets the 'solid' literals check
    // without an assertion.
    const style: StyleObject = {
      paddingTop: '4px',
      paddingBottom: '4px',
      paddingLeft: '16px',
      paddingRight: '16px',
      fontSize: '16px',
      lineHeight: '16px',
      backgroundColor,
      color: isActive ? palette.belizeHole : palette.grey70,
      ...solidBorder(borderColor),
      borderTopLeftRadius: side === 'left' ? '4px' : '0',
      borderBottomLeftRadius: side === 'left' ? '4px' : '0',
      borderTopRightRadius: side === 'right' ? '4px' : '0',
      borderBottomRightRadius: side === 'right' ? '4px' : '0',
      ':hover': { backgroundColor },
    }
    return { BaseButton: { style } }
  }

  // FR-76: the whole preview column collapses to a faded, non-interactive
  // In-App mock (default Placed/Preparing/Ready track — no merchant edits, no
  // channel logic) with a locked overlay card on top. No tabs, no platform
  // toggle, no "preview as you edit" row (Figma 1000:4376 / 1087:16091).
  if (locked) {
    return (
      <div
        className={css({
          display: 'flex',
          justifyContent: 'center',
          padding: '32px 24px',
          backgroundColor: palette.white,
        })}
      >
        <div className={css({ position: 'relative' })}>
          <div
            aria-hidden="true"
            className={css({ opacity: LOCKED_MOCK_OPACITY })}
          >
            {/* isFlybuyProvider intentionally omitted (defaults false): FR-76
                already strips every other merchant/config-specific fact from
                this faded mock (disabledStages, real content) — the check-in
                row would be the one leftover config-specific detail. */}
            <InAppTracker
              content={content}
              deliveryCapable={deliveryCapable}
              disabledStages={new Set()}
            />
          </div>
          <div
            data-testid="lu-preview-locked-overlay"
            className={css({
              position: 'absolute',
              top: '32px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '85%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              padding: '20px 16px',
              borderRadius: '12px',
              backgroundColor: palette.grey10,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              textAlign: 'center',
            })}
          >
            <span aria-hidden="true" className={css({ display: 'flex' })}>
              <Lock fillColor={palette.grey90} />
            </span>
            <Text.BodyText3 bold color="grey90">
              {t('locked_overlay')}
            </Text.BodyText3>
          </div>
        </div>
      </div>
    )
  }

  const cards = (
    <>
      {pushDisabled && <PushDisabledNotice />}
      {hasPush && (
        <PushNotificationCard
          content={content}
          platform={platform}
          appIconUrl={appIconUrl}
        />
      )}
      {hasLiveActivity && (
        <LiveActivityCard
          content={content}
          platform={platform}
          deliveryCapable={deliveryCapable}
          merchantName={merchantName}
          appIconUrl={appIconUrl}
          disabledStages={disabledStages}
        />
      )}
    </>
  )

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        padding: '32px 24px',
        backgroundColor: palette.white,
      })}
    >
      {/* Segmented tab pair */}
      <div
        role="tablist"
        aria-label={t('tabs_label')}
        className={css({ display: 'flex', width: '100%' })}
      >
        <button
          type="button"
          role="tab"
          id={PUSH_TAB_ID}
          aria-selected={isPushTab}
          aria-controls={PANEL_ID}
          onClick={() => setTab('push_live_activity')}
          className={tabPillClass(isPushTab, 'left')}
        >
          {t('push_live_activity')}
        </button>
        <button
          type="button"
          role="tab"
          id={IN_APP_TAB_ID}
          aria-selected={!isPushTab}
          aria-controls={PANEL_ID}
          onClick={() => setTab('in_app')}
          className={tabPillClass(!isPushTab, 'right')}
        >
          {t('in_app')}
        </button>
      </div>

      {/* "Preview updates as you edit" indicator + platform toggle */}
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '32px',
        })}
      >
        <div
          className={css({ display: 'flex', alignItems: 'center', gap: '8px' })}
        >
          <div
            aria-hidden="true"
            className={css({
              width: STATUS_DOT_SIZE,
              height: STATUS_DOT_SIZE,
              borderRadius: '50%',
              backgroundColor: palette.spearmint50,
            })}
          />
          <Text.BodyText4 color="grey70">
            {t('preview_as_you_edit')}
          </Text.BodyText4>
        </div>

        {/* Platform toggle is only meaningful for the device-mock tab. */}
        {isPushTab && (
          <div
            role="group"
            aria-label={t('platform_toggle_label')}
            className={css({ display: 'flex' })}
          >
            <Button
              kind="minimal"
              aria-label={t('ios')}
              aria-pressed={platform === 'ios'}
              onClick={() => setPlatform('ios')}
              overrides={platformButtonOverrides(platform === 'ios', 'left')}
            >
              <BrandApple />
            </Button>
            <Button
              kind="minimal"
              aria-label={t('android')}
              aria-pressed={platform === 'android'}
              onClick={() => setPlatform('android')}
              overrides={platformButtonOverrides(
                platform === 'android',
                'right'
              )}
            >
              <BrandAndroid />
            </Button>
          </div>
        )}
      </div>

      {/* Active preview */}
      <div
        role="tabpanel"
        id={PANEL_ID}
        aria-labelledby={isPushTab ? PUSH_TAB_ID : IN_APP_TAB_ID}
        className={css({ display: 'flex', justifyContent: 'center' })}
      >
        {isPushTab ? (
          platform === 'ios' ? (
            <IosLockScreen>{cards}</IosLockScreen>
          ) : (
            <AndroidLockScreen>{cards}</AndroidLockScreen>
          )
        ) : (
          <InAppTracker
            content={content}
            deliveryCapable={deliveryCapable}
            disabledStages={disabledStages}
            isFlybuyProvider={isFlybuyProvider}
          />
        )}
      </div>

      {/* Applies to both the lock-screen Live Activity (either platform tab)
          and the in-app tracker, so it renders once here rather than inside
          `cards` (which is push/live-activity-tab only). */}
      <AppVersionNotice />
    </div>
  )
}

export default LiveUpdatesPreview
